import { COLOR_HEX } from "../../domain/canvas-model";
import { ACCENT_HEX, type TemplateDef, type Spec } from "./templates";
import { cn } from "../cn";
import { useI18n } from "../../i18n";

/**
 * Schematic mini-preview of a Canvas template's normalized layout (0..100).
 * Pure SVG, no backend — text modules read as tinted bars, shapes as their
 * real geometry, links/social as accented chips. Communicates composition,
 * hierarchy and rhythm without pretending to be the final render.
 */
function specFill(s: Spec, accent: string): string {
  if (s.fill) return COLOR_HEX[s.fill] ?? "#556071";
  if (s.color) return COLOR_HEX[s.color] ?? accent;
  return accent;
}

export function TemplatePreview({ def, className }: { def: TemplateDef; className?: string }) {
  const { t } = useI18n();
  const accent = ACCENT_HEX[def.accent];
  const bg = def.style.background_mode === "SOLID_COLOR"
    ? (COLOR_HEX[def.style.background_color ?? "GRID_OBSIDIAN"] ?? "#0A0F14")
    : "#0A0F14";
  const perim = COLOR_HEX[def.style.perimeter_color] ?? accent;
  return (
    <svg
      data-testid={`template-preview-${def.id}`}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className={cn("block w-full h-full", className)}
      role="img"
      aria-label={t("editor.templates.previewAria", { name: t(`editor.templates.catalog.${def.id}.name`) })}
    >
      <rect x="0" y="0" width="100" height="100" fill={bg} />
      <rect x="1.2" y="1.2" width="97.6" height="97.6" fill="none" stroke={perim}
        strokeOpacity={def.style.perimeter_glow === "OFF" ? 0.4 : 0.85} strokeWidth="0.8" />
      {def.specs.map((s, i) => {
        const k = `${def.id}-${i}`;
        if (s.kind === "line") {
          return <line key={k} x1={s.x} y1={s.y} x2={s.x + s.w} y2={s.y} stroke={specFill(s, accent)} strokeOpacity={0.7} strokeWidth="0.8" />;
        }
        if (s.kind === "ellipse") {
          return <ellipse key={k} cx={s.x + s.w / 2} cy={s.y + s.h / 2} rx={s.w / 2} ry={s.h / 2} fill={specFill(s, accent)} opacity={(s.opacity ?? 1) * 0.9} />;
        }
        if (s.kind === "rect" || s.kind === "round") {
          return <rect key={k} x={s.x} y={s.y} width={s.w} height={s.h} rx={s.kind === "round" ? 2 : 0} fill={specFill(s, accent)} opacity={(s.opacity ?? 1) * 0.9} />;
        }
        if (s.kind === "link") {
          return (
            <g key={k}>
              <rect x={s.x} y={s.y} width={s.w} height={s.h} rx={s.h / 2} fill={accent} opacity={0.85} />
            </g>
          );
        }
        if (s.kind === "social" || s.kind === "contacts") {
          const n = 3;
          const d = s.w / (n + 1);
          return (
            <g key={k}>
              {Array.from({ length: n }).map((_, j) => (
                <circle key={j} cx={s.x + d * (j + 1)} cy={s.y + s.h / 2} r={Math.min(s.h / 2.4, 2.4)} fill={accent} opacity={0.75} />
              ))}
            </g>
          );
        }
        // text bars — title bolder/taller, description = stacked thin lines
        const col = specFill(s, accent);
        if (s.kind === "description") {
          const rows = 3;
          const gap = s.h / rows;
          return (
            <g key={k}>
              {Array.from({ length: rows }).map((_, r) => (
                <rect key={r} x={s.x} y={s.y + gap * r + gap * 0.2} width={s.w * (r === rows - 1 ? 0.6 : 1)} height={Math.max(0.9, gap * 0.4)} rx={0.6} fill={col} opacity={0.5} />
              ))}
            </g>
          );
        }
        return <rect key={k} x={s.x} y={s.y} width={s.w} height={s.h} rx={0.8} fill={col} opacity={s.kind === "title" ? 0.92 : 0.62} />;
      })}
    </svg>
  );
}
